import type { UnifiedPost } from "@/lib/blog-db";

/**
 * Score = number of shared tags. Tie-broken by recency (newer first).
 * Falls back to most recent posts when no tag overlap exists.
 */
export function pickRelatedPosts(
  current: UnifiedPost,
  all: UnifiedPost[],
  limit = 3,
): UnifiedPost[] {
  const others = all.filter((p) => p.slug !== current.slug);
  const currentTags = new Set((current.tags ?? []).map((t) => t.toLowerCase()));

  const scored = others.map((p) => {
    const overlap = (p.tags ?? []).reduce(
      (acc, t) => acc + (currentTags.has(t.toLowerCase()) ? 1 : 0),
      0,
    );
    return { post: p, overlap };
  });

  scored.sort((a, b) => {
    if (b.overlap !== a.overlap) return b.overlap - a.overlap;
    return a.post.date < b.post.date ? 1 : -1;
  });

  return scored.slice(0, limit).map((s) => s.post);
}

export interface TocItem {
  id: string;
  text: string;
}

export function slugifyHeading(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 80);
}

/** Extract H2 headings from a markdown string (ignores fenced code blocks). */
export function extractMarkdownHeadings(md: string): TocItem[] {
  const lines = md.split("\n");
  const items: TocItem[] = [];
  let inFence = false;
  const seen = new Map<string, number>();
  for (const raw of lines) {
    const line = raw.trimEnd();
    if (/^```/.test(line)) {
      inFence = !inFence;
      continue;
    }
    if (inFence) continue;
    const m = /^##\s+(.+?)\s*#*\s*$/.exec(line);
    if (!m) continue;
    const text = m[1].replace(/\*\*|__|`|\[(.+?)\]\(.+?\)/g, "$1").trim();
    const base = slugifyHeading(text);
    const n = (seen.get(base) ?? 0) + 1;
    seen.set(base, n);
    items.push({ id: n === 1 ? base : `${base}-${n}`, text });
  }
  return items;
}

/** Extract H2 headings from seed-style body blocks. */
export function extractBlockHeadings(
  blocks: { type: string; text?: string }[] | undefined,
): TocItem[] {
  if (!blocks) return [];
  const items: TocItem[] = [];
  const seen = new Map<string, number>();
  for (const b of blocks) {
    if (b.type !== "h2" || !b.text) continue;
    const base = slugifyHeading(b.text);
    const n = (seen.get(base) ?? 0) + 1;
    seen.set(base, n);
    items.push({ id: n === 1 ? base : `${base}-${n}`, text: b.text });
  }
  return items;
}
